import React, { useRef, useState } from 'react';
import { getMediafilePresentationKind, MediafileItem } from '@entities/media';
import IconCopy from '@zvuk-b2b/react-uikit/ui/icons/Copy';
import IconTrash2 from '@zvuk-b2b/react-uikit/ui/icons/Trash2';
import { TEXTS } from '../../texts';
import { TIME_PATTERN } from '../../consts';
import { parseTimeString } from '../durationUtils';
import { DATA_TEST_IDS } from './consts';
import MediafilePosterAdapter from './MediafilePosterAdapter';
import MediafileDurationControl from './MediafileDurationControl';
import type { MediafileItemAdapterProps } from './types';

const MediafileItemAdapter: React.FC<MediafileItemAdapterProps> = ({
  file,
  leading,
  className,
  onSetDuration,
  onDraftDurationChange,
  onRemoveMediafile,
  onCopyMediafile,
  onPreview,
  isBlockedDurationEdit = false,
}) => {
  const isEmenu = file.itemType === 'emenu';
  const duration = file.itemType === 'emenu' ? file.generationDuration : file.duration;
  const title = file.title ?? '';
  const kind = file.itemType === 'emenu' ? 'emenu' : getMediafilePresentationKind(file);
  const [isExpanded, setIsExpanded] = useState(!isEmenu && !file.playDuration);
  const [isValid, setIsValid] = useState(isEmenu || Number(file.playDuration) > 0);
  const [isErrorVisible, setIsErrorVisible] = useState(!isValid);
  const latestValidityRef = useRef(isValid);

  const updateValidity = (newDuration: number, shouldValidate = false) => {
    const nextIsValid = Number(newDuration) > 0;
    latestValidityRef.current = nextIsValid;
    setIsValid(nextIsValid);
    setIsErrorVisible((wasVisible) => !nextIsValid && (shouldValidate || wasVisible));
    if (!nextIsValid) setIsExpanded(true);
  };

  const getDuration = (value: string) => {
    if (!duration) return TIME_PATTERN.test(value) ? parseTimeString(value).milliseconds : 0;
    const repeatCount = Number(value);
    return Number.isInteger(repeatCount) && repeatCount >= 1 ? repeatCount * duration : 0;
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (isEmenu) return;
    const nextDuration = getDuration(event.target.value);
    updateValidity(nextDuration, true);
    onSetDuration(file.id, nextDuration);
  };

  return (
    <MediafileItem
      id={file.id}
      kind={kind}
      state="ready"
      title={title}
      poster={
        <MediafilePosterAdapter
          file={file}
          kind={kind}
          onPreview={onPreview}
        />
      }
      leading={leading}
      className={className}
      settings={{
        panelId: DATA_TEST_IDS.PANEL(file.id),
        label: TEXTS.SETTINGS,
        layout: 'stacked',
        isExpanded,
        onExpandedChange: (nextExpanded) => setIsExpanded(nextExpanded || !latestValidityRef.current),
        content: (
          <MediafileDurationControl
            fileId={file.id}
            duration={duration}
            playDuration={file.playDuration}
            isValid={isValid}
            isErrorVisible={isErrorVisible}
            isDisabled={isEmenu}
            isBlockedDurationEdit={!isEmenu && isBlockedDurationEdit}
            onBlur={handleBlur}
            onMouseOut={() => !isEmenu && setIsErrorVisible(!latestValidityRef.current)}
            onChange={(value) => {
              const nextDuration = getDuration(value);
              updateValidity(nextDuration);
              onDraftDurationChange?.(file.id, nextDuration);
            }}
          />
        ),
      }}
      actions={
        isEmenu
          ? undefined
          : [
              {
                id: 'copy',
                kind: 'button',
                label: TEXTS.DUPLICATE,
                icon: <IconCopy />,
                onClick: () => onCopyMediafile(file.id),
              },
              {
                id: 'remove',
                kind: 'button',
                label: TEXTS.REMOVE_FROM_VIDEO_STREAM,
                icon: <IconTrash2 />,
                onClick: () => onRemoveMediafile(file.id),
              },
            ]
      }
    />
  );
};

export default MediafileItemAdapter;
